/**
 * WCAG 2.1 contrast, measured (ENG-2055 AC 5).
 *
 * `palette.getContrastText` is not this: it keeps white text wherever white reaches the
 * theme's `contrastThreshold`, which this app leaves at MUI's default of 3. On the colour
 * picker's own preset blue that is 3.29:1, under the 4.5:1 that 12.5–14px text needs.
 */

export type Rgb = [number, number, number];

/** WCAG 2.1 AA for text under 18pt (or 14pt bold), which is every line on a card. */
export const AA_TEXT = 4.5;

/**
 * WCAG 1.4.11 non-text contrast: what a white card's status border, and the "scheduling
 * for" page frame, must reach on white.
 */
export const NON_TEXT = 3;
const VISIBLE_STEP = 0.05;

const HEX = /^#([0-9a-f]{3,8})$/i;
const RGB_FN = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i;

/**
 * An appointment type's colour as channels, or `null` when it is not a colour this can
 * read. The settings form saves `#rrggbb` from a free picker; imported data is not held to
 * that, so a caller must have an answer for `null` rather than a guess.
 */
export function parseColor(css: string | null | undefined): Rgb | null {
  const text = css?.trim() ?? "";
  const hex = HEX.exec(text)?.[1];
  if (hex) {
    if (hex.length === 3 || hex.length === 4)
      return [0, 1, 2].map((i) => parseInt(hex[i] + hex[i], 16)) as Rgb;
    if (hex.length === 6 || hex.length === 8)
      return [0, 2, 4].map((i) => parseInt(hex.slice(i, i + 2), 16)) as Rgb;
    return null;
  }
  const fn = RGB_FN.exec(text);
  return fn ? (fn.slice(1, 4).map(Number) as Rgb) : null;
}

function relativeLuminance([r, g, b]: Rgb): number {
  const linear = (channel: number) => {
    const v = channel / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  };
  return 0.2126 * linear(r) + 0.7152 * linear(g) + 0.0722 * linear(b);
}

/** 1 (identical) to 21 (black on white); the order of the two colours does not matter. */
export function contrastRatio(a: Rgb, b: Rgb): number {
  const [hi, lo] = [relativeLuminance(a), relativeLuminance(b)].sort(
    (x, y) => y - x
  );
  return (hi + 0.05) / (lo + 0.05);
}

/** `over` painted on `under` at `amount` opacity, rounded to whole channels. */
export function mixColors(over: Rgb, under: Rgb, amount: number): Rgb {
  return under.map((v, i) =>
    Math.round(over[i] * amount + v * (1 - amount))
  ) as Rgb;
}

/**
 * Whichever ink reads better on `ground`. With a white and a black ink the result never
 * measures below ~4.58:1 — the two are equal there, and every other ground favours one of
 * them — so AA holds for any colour a practice can type.
 */
export function readableTextOn<T extends Rgb>(
  ground: Rgb,
  light: T,
  dark: T
): T {
  return contrastRatio(light, ground) >= contrastRatio(dark, ground)
    ? light
    : dark;
}

/**
 * `colour` stepped toward `dark` only as far as it takes to reach 3:1 on `paper`, so a pale
 * type colour's border (or frame) stays visible and keeps its hue. `null` when the colour
 * already reaches it — the caller then keeps the catalog's own string.
 */
export function visibleOn(paper: Rgb, colour: Rgb, dark: Rgb): string | null {
  if (contrastRatio(colour, paper) >= NON_TEXT) return null;
  let amount = 0;
  let stepped = colour;
  while (contrastRatio(stepped, paper) < NON_TEXT && amount < 1) {
    amount = Math.min(1, amount + VISIBLE_STEP);
    stepped = mixColors(dark, colour, amount);
  }
  return `rgb(${stepped.join(", ")})`;
}
